import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import cx from './cx';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Field';

/* ------------------------------------------------------------------ Toasts */

const ToastContext = createContext(null);
const TOAST_ICONS = { success: CheckCircle2, error: XCircle, info: Info, warning: AlertTriangle };
const TOAST_TONES = { success: 'text-success', error: 'text-loss', info: 'text-accent', warning: 'text-warn' };

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((toast) => toast.id !== id)), []);

  const push = useCallback((input) => {
    const toast = typeof input === 'string' ? { message: input } : input;
    idRef.current += 1;
    const id = idRef.current;
    const tone = toast.tone || 'info';
    setToasts((list) => [...list.slice(-3), { ...toast, tone, id }]);
    const duration = toast.duration ?? (tone === 'error' ? 7000 : 4000);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const api = useMemo(() => {
    const fn = (input) => push(input);
    fn.success = (message, opts) => push({ ...opts, message, tone: 'success' });
    fn.error = (message, opts) => push({ ...opts, message, tone: 'error' });
    fn.info = (message, opts) => push({ ...opts, message, tone: 'info' });
    fn.warning = (message, opts) => push({ ...opts, message, tone: 'warning' });
    fn.dismiss = dismiss;
    return fn;
  }, [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[90] flex flex-col items-center gap-2 px-4 lg:bottom-6 lg:items-end lg:px-6"
      >
        {toasts.map((toast) => {
          const Icon = TOAST_ICONS[toast.tone] || Info;
          return (
            <div
              key={toast.id}
              role={toast.tone === 'error' ? 'alert' : 'status'}
              className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-panel border border-line bg-surface-raised px-4 py-3 text-sm text-fg shadow-pop"
            >
              <Icon className={cx('mt-0.5 h-4 w-4 shrink-0', TOAST_TONES[toast.tone])} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                {toast.title && <p className="font-medium">{toast.title}</p>}
                <p className={toast.title ? 'text-fg-muted' : undefined}>{toast.message}</p>
                {toast.action && (
                  <button type="button" onClick={() => { toast.action.onClick(); dismiss(toast.id); }} className="mt-1 text-sm font-medium text-accent hover:underline">
                    {toast.action.label}
                  </button>
                )}
              </div>
              <button type="button" onClick={() => dismiss(toast.id)} aria-label="Dismiss notification" className="-me-1 inline-flex h-9 w-9 items-center justify-center rounded-control text-fg-muted hover:bg-surface-2 hover:text-fg">
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

const noopToast = Object.assign(() => {}, { success() {}, error() {}, info() {}, warning() {}, dismiss() {} });

/** `const toast = useToast(); toast.success('Saved')`. Safe to call outside the provider (no-op). */
export function useToast() {
  return useContext(ToastContext) || noopToast;
}

/* ----------------------------------------------------------------- Confirm */

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [typed, setTyped] = useState('');
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setTyped('');
    setState(typeof options === 'string' ? { title: options } : options);
  }), []);

  const close = (result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setState(null);
  };

  const needsText = Boolean(state?.requireText);
  const canConfirm = !needsText || typed.trim() === state.requireText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={Boolean(state)}
        onClose={() => close(false)}
        title={state?.title || 'Are you sure?'}
        description={state?.description}
        size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => close(false)}>{state?.cancelLabel || 'Cancel'}</Button>
            <Button
              variant={state?.tone === 'danger' ? 'danger' : 'primary'}
              disabled={!canConfirm}
              onClick={() => close(true)}
              data-autofocus={needsText ? undefined : true}
            >
              {state?.confirmLabel || 'Confirm'}
            </Button>
          </>
        )}
      >
        {state?.body}
        {needsText && (
          <form onSubmit={(event) => { event.preventDefault(); if (canConfirm) close(true); }}>
            <label htmlFor="confirm-typed" className="mb-1.5 block text-xs font-medium text-fg-muted">
              Type <span className="font-mono font-semibold text-fg">{state.requireText}</span> to confirm
            </label>
            <Input id="confirm-typed" value={typed} onChange={(event) => setTyped(event.target.value)} autoComplete="off" data-autofocus />
          </form>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

/**
 * `const confirm = useConfirm(); if (await confirm({ title, description, tone: 'danger', confirmLabel, requireText }))`.
 * Outside the provider it resolves to true, which keeps isolated component tests working.
 */
export function useConfirm() {
  return useContext(ConfirmContext) || (async () => true);
}
